import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

/**
 * Base query with credentials for cookies
 * @type {BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError, {}, FetchBaseQueryMeta>}
 */
const baseQuery = fetchBaseQuery({
  baseUrl: process.env.REACT_APP_PORTAL_API + '/api/v1/',
  credentials: 'include',
});

/**
 * Custom base query with 401 handling.
 * If we get a 401 error, clear session and redirect to login.
 * Clear both storages
 * @param args
 * @param api
 * @param extraOptions
 * @returns {Promise<*>}
 */
const baseQueryWithAuth = async (args, api, extraOptions) => {
  const result = await baseQuery(args, api, extraOptions);

  if (result.error && result.error.status === 401) {
    sessionStorage.clear();
    localStorage.clear();
    window.location.href = '/login';
  }

  return result;
};

/**
 * Define a service using a base URL and expected endpoints.
 */
export const portalApi = createApi({
  reducerPath: 'portalApi',
  baseQuery: baseQueryWithAuth,
  keepUnusedDataFor: 30,
  refetchOnMountOrArgChange: 30,
  tagTypes: [
    'Auth',
    'Organizations',
    'Users',
    'UserReports',
    'Workspaces',
    'Reports',
    'Tenants',
    'Activities',
    'Settings',
    'Support',
    'SecurityGroups',
    'EmbedToken'
  ],
  endpoints: (builder) => ({
    addAuthForgotPassword: builder.mutation({
      query(data) {
        return {
          url: 'auth/forgot-password',
          method: 'POST',
          body: data
        };
      },
      invalidatesTags: ['Auth']
    }),
    getAuthResetPassword: builder.query({
      query: (arg) => {
        const { email, token } = arg;
        return {
          url: 'auth/reset-password',
          params: { email, token }
        };
      },
      providesTags: ['Auth']
    }),
    addAuthResetPassword: builder.mutation({
      query(data) {
        return {
          url: 'auth/reset-password',
          method: 'POST',
          body: data
        };
      },
      invalidatesTags: ['Auth']
    }),
    getOrganizations: builder.query({
      query: () => 'organizations',
      providesTags: ['Organizations']
    }),
    getOrganization: builder.query({
      query: (organizationId) => `organizations/${organizationId}`,
      providesTags: ['Organizations']
    }),
    addOrganization: builder.mutation({
      query(data) {
        return {
          url: 'organizations',
          method: 'POST',
          body: data
        };
      },
      invalidatesTags: ['Organizations']
    }),
    updateOrganization: builder.mutation({
      query({ organization_id: id, ...patch }) {
        return {
          url: `organizations/${id}`,
          method: 'PUT',
          body: patch
        };
      },
      invalidatesTags: ['Organizations']
    }),
    getUsers: builder.query({
      query: () => 'users',
      providesTags: ['Users']
    }),
    getUser: builder.query({
      query: (userId) => `users/${userId}`,
      providesTags: ['Users']
    }),
    addUser: builder.mutation({
      query(data) {
        return {
          url: 'users',
          method: 'POST',
          body: data
        };
      },
      invalidatesTags: ['Users']
    }),
    updateUserAccount: builder.mutation({
      query({ user_id: id, ...patch }) {
        return {
          url: `users/account/${id}`,
          method: 'PUT',
          body: patch
        };
      },
      invalidatesTags: ['Users']
    }),
    updateUserStatus: builder.mutation({
      query({ user_id: id, ...patch }) {
        return {
          url: `users/status/${id}`,
          method: 'PUT',
          body: patch
        };
      },
      invalidatesTags: ['Users']
    }),
    getUserReports: builder.query({
      query: (userId) => `userreports/reports/${userId}`,
      providesTags: ['UserReports']
    }),
    updateUserReportViewPriviledges: builder.mutation({
      query(data) {
        return {
          url: `userreports/reports/${data.user_id}`,
          method: 'POST',
          body: data
        };
      },
      invalidatesTags: ['Reports', 'UserReports']
    }),
    getWorkspaces: builder.query({
      query: () => 'workspaces',
      providesTags: ['Workspaces']
    }),
    getWorkspacesByOrganization: builder.query({
      query: (organizationId) => `workspaces/organization/${organizationId}`,
      providesTags: ['Workspaces']
    }),
    getWorkspace: builder.query({
      query: (workspaceId) => `workspaces/${workspaceId}`,
      providesTags: ['Workspaces']
    }),
    addWorkspace: builder.mutation({
      query(data) {
        return {
          url: 'workspaces',
          method: 'POST',
          body: data
        };
      },
      invalidatesTags: ['Workspaces']
    }),
    updateWorkspace: builder.mutation({
      query({ workspace_id: id, ...patch }) {
        return {
          url: `workspaces/${id}`,
          method: 'PUT',
          body: patch
        };
      },
      invalidatesTags: ['Workspaces']
    }),
    getReports: builder.query({
      query: () => 'reports',
      providesTags: ['Reports']
    }),
    getReport: builder.query({
      query: (reportId) => `reports/${reportId}`,
      providesTags: ['Reports']
    }),
    addReport: builder.mutation({
      query(data) {
        return {
          url: 'reports',
          method: 'POST',
          body: data
        };
      },
      invalidatesTags: ['Reports']
    }),
    updateReport: builder.mutation({
      query({ report_id: id, ...patch }) {
        return {
          url: `reports/${id}`,
          method: 'PUT',
          body: patch
        };
      },
      invalidatesTags: ['Reports']
    }),
    updateReportStatus: builder.mutation({
      query({ report_id: id, ...patch }) {
        return {
          url: `reports/status/${id}`,
          method: 'PUT',
          body: patch
        };
      },
      invalidatesTags: ['Reports']
    }),
    getTenants: builder.query({
      query: () => 'tenants',
      providesTags: ['Tenants']
    }),
    addActivityReport: builder.mutation({
      query(data) {
        return {
          url: 'activities/report',
          method: 'POST',
          body: data
        };
      }
    }),
    getAppSettings: builder.query({
      query: (app_id) => `settings/${app_id}`,
      providesTags: ['Settings']
    }),
    updateAppSettings: builder.mutation({
      query({ ...patch }) {
        return {
          url: 'settings',
          method: 'PUT',
          body: patch
        };
      },
      invalidatesTags: ['Settings']
    }),
    getSMTPSettings: builder.query({
      query: (organization_id) => `settings/admin/smtp/${organization_id}`,
      providesTags: ['Settings']
    }),
    addSMTPSettings: builder.mutation({
      query({ ...patch }) {
        return {
          url: 'settings/admin/smtp',
          method: 'POST',
          body: patch
        };
      },
      invalidatesTags: ['Settings']
    }),
    addTestMail: builder.mutation({
      query({ ...patch }) {
        //console.log('here');
        return {
          url: 'settings/admin/smtp/mailtest',
          method: 'POST',
          body: patch
        };
      },
      invalidatesTags: ['Settings']
    }),
    addTicket: builder.mutation({
      query(data) {
        return {
          url: 'support/ticket',
          method: 'POST',
          body: data
        };
      },
      invalidatesTags: ['Support']
    }),

    importUsersFromExcel: builder.mutation({
      query: ({ fileData, fileName, sendEmails = true }) => ({
        url: 'users/import',
        method: 'POST',
        body: {
          fileData,
          fileName,
          sendEmails
        }
      }),
      invalidatesTags: ['Users']
    }),

    downloadImportTemplate: builder.mutation({
      query: () => ({
        url: 'users/import/template',
        method: 'GET',
        responseHandler: (response) => response.blob()
      }),
      keepUnusedDataFor: 0
    }),
    getAvailableReportCodes: builder.query({
      query: (organization_id) => `reports/AvailableReportCodes/${organization_id}`,
      providesTags: ['Reports']
    }),
    getAvailableWorkspaces: builder.query({
      query: (organization_id) => `reports/AvailableWorkspaces/${organization_id}`,
      providesTags: ['Reports']
    }),
    getDatasetTables: builder.query({
  query: ({ workspaceId, datasetId }) => `reports/${workspaceId}/${datasetId}/Tables`
}),
    getRoless: builder.query({
      query: (user_id) => `Users/Roles/Type/${user_id}`,
      providesTags: ['Users']
    }),
    getSignIn: builder.query({
      query: (user_id) => `Users/Roles/SignIn/${user_id}`,
      providesTags: ['Users']
    }),
    getUserInfo: builder.query({
      query: (report_id) => `reports/UserInfo/${report_id}`,
      providesTags: ['Reports']
    }),

    // Embed token with caching - tokens valid for ~1 hour
    getEmbedToken: builder.query({
      query: ({ workspaceId, workspaceCode, reportId, reportCode }) =>
        `reports/${workspaceId}/${workspaceCode}/${reportId}/${reportCode}/GenerateToken`,
      providesTags: ['EmbedToken'],
      keepUnusedDataFor: 3600 // Cache for 1 hour
    }),

    // portalApi endpoints...
exportExcelDax: builder.mutation({
  query: ({ workspaceId, datasetId, payload }) => ({
    url: `reports/${workspaceId}/${datasetId}/ExportExcel`,
    method: 'POST',
    body: payload,
    // tell RTKQ to give us a Blob instead of trying to parse JSON
    responseHandler: (response) => response.blob(),
  }),
}),





    // Security Groups endpoints
    getSecurityGroups: builder.query({
      query: (organizationId) => ({
        url: 'security-groups',
        params: organizationId ? { organization_id: organizationId } : {}
      }),
      providesTags: ['SecurityGroups']
    }),

    getSecurityGroupById: builder.query({
      query: (securityGroupId) => `security-groups/${securityGroupId}`,
      providesTags: ['SecurityGroups']
    }),

    addSecurityGroup: builder.mutation({
      query: (data) => ({
        url: 'security-groups',
        method: 'POST',
        body: data
      }),
      invalidatesTags: ['SecurityGroups']
    }),

    updateSecurityGroup: builder.mutation({
      query: ({ securityGroupId, ...data }) => ({
        url: `security-groups/${securityGroupId}`,
        method: 'PUT',
        body: data
      }),
      invalidatesTags: ['SecurityGroups']
    }),

    deleteSecurityGroup: builder.mutation({
      query: (securityGroupId) => ({
        url: `security-groups/${securityGroupId}`,
        method: 'DELETE'
      }),
      invalidatesTags: ['SecurityGroups']
    }),

    getUsersBySecurityGroup: builder.query({
      query: (securityGroupId) => `security-groups/${securityGroupId}/users`,
      providesTags: ['SecurityGroups']
    }),

    getSecurityGroupsByUser: builder.query({
      query: (userId) => `security-groups/users/${userId}/security-groups`,
      providesTags: ['SecurityGroups']
    }),

    assignUsersToSecurityGroup: builder.mutation({
      query: ({ securityGroupId, user_ids }) => ({
        url: `security-groups/${securityGroupId}/users`,
        method: 'POST',
        body: { user_ids }
      }),
      invalidatesTags: ['SecurityGroups']
    }),

    getAvailableUsersForSecurityGroup: builder.query({
      query: ({ securityGroupId, organizationId }) => ({
        url: `security-groups/${securityGroupId}/available-users`,
        params: { organization_id: organizationId }
      }),
      providesTags: ['SecurityGroups']
    }),

    getReportsBySecurityGroup: builder.query({
      query: (securityGroupId) => `security-groups/${securityGroupId}/reports`,
      providesTags: ['SecurityGroups']
    }),

    assignReportsToSecurityGroup: builder.mutation({
      query: ({ securityGroupId, report_ids }) => ({
        url: `security-groups/${securityGroupId}/reports`,
        method: 'POST',
        body: { report_ids }
      }),
      invalidatesTags: ['SecurityGroups']
    }),

    getAvailableReportsForSecurityGroup: builder.query({
      query: ({ securityGroupId, organizationId }) => ({
        url: `security-groups/${securityGroupId}/available-reports`,
        params: { organization_id: organizationId }
      }),
      providesTags: ['SecurityGroups']
    })
  })
});

export const {
  useGetUserInfoQuery,
  useGetDatasetTablesQuery,
  useAddAuthForgotPasswordMutation,
  useGetAuthResetPasswordQuery,
  useAddAuthResetPasswordMutation,
  useGetOrganizationsQuery,
  useGetRolessQuery,
  useGetSignInQuery,
  useGetOrganizationQuery,
  useAddOrganizationMutation,
  useUpdateOrganizationMutation,
  useGetUsersQuery,
  useGetUserQuery,
  useAddUserMutation,
  useUpdateUserAccountMutation,
  useUpdateUserStatusMutation,
  useGetUserReportsQuery,
  useUpdateUserReportViewPriviledgesMutation,
  useGetWorkspacesQuery,
  useGetWorkspacesByOrganizationQuery,
  useGetAvailableReportCodesQuery,
  useGetAvailableWorkspacesQuery,
  useGetWorkspaceQuery,
  useAddWorkspaceMutation,
  useUpdateWorkspaceMutation,
  useGetReportsQuery,
  useGetReportQuery,
  useAddReportMutation,
  useUpdateReportMutation,
  useUpdateReportStatusMutation,
  useGetTenantsQuery,
  useAddActivityReportMutation,
  useGetAppSettingsQuery,
  useLazyGetAppSettingsQuery,
  useUpdateAppSettingsMutation,
  useGetSMTPSettingsQuery,
  useAddTestMailMutation,
  useAddSMTPSettingsMutation,
  useExportExcelDaxMutation,
  useAddTicketMutation,
  useImportUsersFromExcelMutation,
  useDownloadImportTemplateMutation,
  useGetSecurityGroupsQuery,
  useGetSecurityGroupByIdQuery,
  useAddSecurityGroupMutation,
  useUpdateSecurityGroupMutation,
  useDeleteSecurityGroupMutation,
  useGetUsersBySecurityGroupQuery,
  useGetSecurityGroupsByUserQuery,
  useAssignUsersToSecurityGroupMutation,
  useGetAvailableUsersForSecurityGroupQuery,
  useGetReportsBySecurityGroupQuery,
  useAssignReportsToSecurityGroupMutation,
  useGetAvailableReportsForSecurityGroupQuery,
  useGetEmbedTokenQuery
} = portalApi;
